import { useState, useCallback, useEffect } from 'react';

export type MicrophonePermissionState = 'granted' | 'denied' | 'prompt' | 'checking' | 'unsupported';

export function useMicrophonePermission() {
  const [permissionState, setPermissionState] = useState<MicrophonePermissionState>('checking');
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  const checkPermission = useCallback(async (): Promise<MicrophonePermissionState> => {
    // Verifica se o navegador suporta a API de permissões
    if (!navigator.permissions) {
      console.log('🎤 Permissions API not supported');
      setPermissionState('unsupported');
      return 'unsupported';
    }

    try {
      const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      const state = result.state as MicrophonePermissionState;
      console.log('🎤 Microphone permission state:', state);
      setPermissionState(state);
      return state;
    } catch (error) {
      console.log('🎤 Error checking microphone permission:', error);
      // Fallback: tenta solicitar acesso para descobrir o estado
      setPermissionState('unsupported');
      return 'unsupported';
    }
  }, []);

  const requestAccess = useCallback(async (): Promise<boolean> => {
    try {
      console.log('🎤 Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Libera o stream imediatamente - só queríamos testar a permissão
      stream.getTracks().forEach(track => track.stop());
      console.log('🎤 Microphone access granted');
      setPermissionState('granted');
      setShowPermissionModal(false);
      return true;
    } catch (error) {
      console.log('🎤 Microphone access denied:', error);
      setPermissionState('denied');
      setShowPermissionModal(true);
      return false;
    }
  }, []);

  const ensureMicrophoneAccess = useCallback(async (): Promise<boolean> => {
    const state = await checkPermission();
    
    if (state === 'granted') {
      return true;
    }
    
    if (state === 'denied') {
      setShowPermissionModal(true);
      return false;
    }
    
    // Se for 'prompt' ou 'unsupported', tenta solicitar
    return await requestAccess();
  }, [checkPermission, requestAccess]);

  // Monitora mudanças na permissão
  useEffect(() => {
    if (!navigator.permissions) return;

    let permissionStatus: PermissionStatus | null = null;

    const setupListener = async () => {
      try {
        permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        
        const handleChange = () => {
          console.log('🎤 Permission changed to:', permissionStatus?.state);
          setPermissionState(permissionStatus?.state as MicrophonePermissionState);
          if (permissionStatus?.state === 'granted') {
            setShowPermissionModal(false);
          }
        };

        permissionStatus.addEventListener('change', handleChange);
      } catch (error) {
        console.log('🎤 Could not setup permission listener:', error);
      }
    };

    setupListener();

    return () => {
      if (permissionStatus) {
        permissionStatus.removeEventListener('change', () => {});
      }
    };
  }, []);

  // Verifica permissão inicial
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  return {
    permissionState,
    showPermissionModal,
    setShowPermissionModal,
    checkPermission,
    requestAccess,
    ensureMicrophoneAccess,
  };
}
